import { Component, EventEmitter, OnInit, Output, ViewEncapsulation, inject } from '@angular/core';

import { InputEmailComponent, UnauthenticatedLayoutComponent } from '@angular-monorepo/ui';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { ForgotPasswordService } from '../../services/forgot-password.service';
import { Response } from '@angular-monorepo/data-access';
import { HttpErrorResponse } from '@angular/common/http';
import { ButtonModule } from 'primeng/button';
import { TranslatePipe, TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'lib-forgot-password',
  imports: [
    InputEmailComponent,
    ReactiveFormsModule,
    ButtonModule,
    TranslatePipe,
    TranslateModule,
    UnauthenticatedLayoutComponent,
  ],
  templateUrl: './forgot-password.component.html',
  styleUrl: './forgot-password.component.css',
  encapsulation: ViewEncapsulation.None,
})
/**
 * Componente `ForgotPasswordComponent`
 *
 * Este componente representa un formulario de recuperación de contraseña.
 * Permite al usuario ingresar su correo electrónico y solicitar un enlace de restablecimiento de contraseña.
 *
 * ### Selector
 * ```
 * <lib-forgot-password (passwordResetResponse)="handleResponse($event)"></lib-forgot-password>
 * ```
 *
 * ### Output
 * - `passwordResetResponse`: Emite un objeto de tipo `Response<boolean>` indicando si el envío fue exitoso o si ocurrió un error.
 *   - Cuando es exitoso: `{ status: 'success', data: { data: true } }`
 *   - Cuando falla: `{ status: 'error', error: HttpErrorResponse }`
 *
 * ### Cómo usarlo
 * 1. Inclúyelo en el módulo correspondiente si no es standalone.
 * 2. Inserta el componente en la plantilla deseada.
 * 3. Escucha el evento `passwordResetResponse` para manejar la respuesta.
 *
 * ### Ejemplo de uso:
 * ```html
 * <lib-forgot-password (passwordResetResponse)="handleResponse($event)"></lib-forgot-password>
 * ```
 *
 * ```ts
 * import { Response } from '@angular-monorepo/data-access';
 * import { HttpErrorResponse } from '@angular/common/http';
 *
 * export class MyComponent {
 *   handleResponse(event: Response<boolean>) {
 *     if (event.status === 'success') {
 *       alert('Enlace enviado con éxito');
 *     } else {
 *       console.error('Error al enviar enlace:', event.error);
 *     }
 *   }
 * }
 * ```
 *
 * ### Accesibilidad
 * Asegúrate de envolver este componente en una sección accesible y de proveer un encabezado contextual si es necesario.
 */
export class ForgotPasswordComponent implements OnInit {
  private fb = inject(FormBuilder);
  private forgotPasswordService = inject(ForgotPasswordService);

  forgotPasswordForm!: FormGroup;

  @Output() passwordResetResponse = new EventEmitter<Response<boolean>>();

  ngOnInit(): void {
    this.forgotPasswordForm = this.fb.group({
      email: ['', [Validators.required]],
    });
  }

  get email(): string {
    return this.forgotPasswordForm.get('email')?.value;
  }

  onSubmit() {
    if (this.forgotPasswordForm.invalid) {
      console.error('Form is invalid');
      return;
    }

    this.forgotPasswordService.sendResetLink(this.email).subscribe({
      next: () => {
        this.handleSuccess();
      },
      error: (error: HttpErrorResponse) => {
        this.handleError(error);
      },
    });
  }

  private handleSuccess(): void {
    console.log('Password reset link sent successfully');
    this.forgotPasswordForm.reset();
    this.passwordResetResponse.emit({
      status: 'success',
    });
  }

  private handleError(error: HttpErrorResponse): void {
    console.error('Error sending password reset link:', error);
    this.passwordResetResponse.emit({ status: 'error', error });
  }
}
